/**
 * Fazla Mesai Ücreti Hesaplama Engine
 * 2026 Türkiye İş Kanunu (4857 sayılı, Madde 41, 46, 47, 63)
 * Güncel SGK oranları ve asgari ücret parametreleri
 */

// ─── 2026 Sabitleri ───────────────────────────────────────────
export const YILI = 2026;
export const BRUT_ASGARI_UCRET = 33_030;
export const NET_ASGARI_UCRET = 28_075.5;
export const SAATLIK_BOLEN = 225; // 30 gün × 7.5 saat
export const YILLIK_FAZLA_MESAI_SINIRI = 270; // saat
export const HAFTALIK_CALISMA_SURESI = 45; // saat
export const GUNLUK_CALISMA_SURESI = 7.5; // saat

// SGK Oranları (2026)
export const SGK_ISCI_ORAN = 0.14; // %14
export const ISSIZLIK_ISCI_ORAN = 0.01; // %1
export const SGK_ISVEREN_ORAN = 0.2375; // %23.75 (MYÖ %12 + GSS %7.5 + KVSK %2.25 + İşsizlik %2)
export const ISSIZLIK_ISVEREN_ORAN = 0.02; // %2
export const DAMGA_VERGISI_ORAN = 0.00759; // %0.759
export const SGK_TAVAN = 297_270;

// Fazla Mesai Katsayıları
export const FAZLA_CALISMA_KATSAYI = 1.5; // %50 zamlı
export const FAZLA_SURELERLE_KATSAYI = 1.25; // %25 zamlı
export const HAFTA_TATILI_KATSAYI = 2.0; // 2 kat
export const RESMI_TATIL_KATSAYI = 2.0; // 2 kat
export const GECE_MESAI_KATSAYI = 1.5; // %50 zamlı (İş Kanunu pratiği)

// ─── Tipler ───────────────────────────────────────────────────
export type MesaiTipi =
  | "fazla_calisma"
  | "fazla_surelerle"
  | "hafta_tatili"
  | "resmi_tatil"
  | "gece_mesai";

export type HaftalikCalismaSuresi = "45_saat" | "45_saat_alti";

export interface FazlaMesaiGirdi {
  brutMaas: number;
  mesaiSaati: number;
  mesaiTipi: MesaiTipi;
  haftalikSure: HaftalikCalismaSuresi;
  sozlesmeHaftalikSaat?: number; // 45 saat altı ise sözleşme süresi
}

export interface FazlaMesaiSonuc {
  // Girdi özeti
  brutMaas: number;
  mesaiSaati: number;
  mesaiTipiLabel: string;
  katsayi: number;

  // Saatlik ücretler
  saatlikBrutUcret: number;
  mesaiSaatlikBrutUcret: number;

  // Brüt mesai ücreti
  toplamBrutMesaiUcreti: number;

  // Kesintiler
  sgkIsciKesinti: number;
  issizlikIsciKesinti: number;
  damgaVergisi: number;
  gelirVergisiMatrahi: number;
  gelirVergisi: number;
  toplamKesinti: number;

  // Net mesai ücreti
  netMesaiUcreti: number;

  // İşveren ek maliyeti
  sgkIsverenMaliyet: number;
  issizlikIsverenMaliyet: number;
  toplamIsverenEkMaliyet: number;
  toplamIsverenMaliyet: number;

  // Yıllık sınır bilgisi
  yillikSinirUyarisi: boolean;
  kalanYillikKota: number;
}

// ─── Yardımcı Fonksiyonlar ───────────────────────────────────

export function getMesaiTipiLabel(tip: MesaiTipi): string {
  const labels: Record<MesaiTipi, string> = {
    fazla_calisma: "Fazla Çalışma (Haftalık 45 Saat Üzeri)",
    fazla_surelerle: "Fazla Sürelerle Çalışma (45 Saat Altı Sözleşme)",
    hafta_tatili: "Hafta Tatili Çalışması",
    resmi_tatil: "Resmi Tatil / Ulusal Bayram Çalışması",
    gece_mesai: "Gece Mesaisi (20:00 – 06:00)",
  };
  return labels[tip];
}

export function getMesaiKatsayi(tip: MesaiTipi): number {
  const katsayilar: Record<MesaiTipi, number> = {
    fazla_calisma: FAZLA_CALISMA_KATSAYI,
    fazla_surelerle: FAZLA_SURELERLE_KATSAYI,
    hafta_tatili: HAFTA_TATILI_KATSAYI,
    resmi_tatil: RESMI_TATIL_KATSAYI,
    gece_mesai: GECE_MESAI_KATSAYI,
  };
  return katsayilar[tip];
}

export function formatTL(value: number): string {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatSaat(saat: number): string {
  return `${saat.toFixed(1)} saat`;
}

// ─── Ana Hesaplama Fonksiyonu ─────────────────────────────────

export function hesaplaFazlaMesai(girdi: FazlaMesaiGirdi): FazlaMesaiSonuc {
  const { brutMaas, mesaiSaati, mesaiTipi } = girdi;

  // Null guard
  const guvenliMaas = Math.max(0, brutMaas || 0);
  const guvenliSaat = Math.max(0, mesaiSaati || 0);

  // 1. Saatlik ücret hesaplama
  const saatlikBrutUcret = guvenliMaas / SAATLIK_BOLEN;

  // 2. Mesai katsayısı
  const katsayi = getMesaiKatsayi(mesaiTipi);
  const mesaiSaatlikBrutUcret = saatlikBrutUcret * katsayi;

  // 3. Toplam brüt mesai ücreti
  const toplamBrutMesaiUcreti = mesaiSaatlikBrutUcret * guvenliSaat;

  // 4. SGK kesintileri (işçi payı)
  const sgkIsciKesinti = toplamBrutMesaiUcreti * SGK_ISCI_ORAN;
  const issizlikIsciKesinti = toplamBrutMesaiUcreti * ISSIZLIK_ISCI_ORAN;

  // 5. Gelir vergisi matrahı
  const gelirVergisiMatrahi =
    toplamBrutMesaiUcreti - sgkIsciKesinti - issizlikIsciKesinti;

  // 6. Gelir vergisi (%15 ilk dilim - mesai ücreti genellikle ilk dilimde kalır)
  const gelirVergisi = Math.max(0, gelirVergisiMatrahi * 0.15);

  // 7. Damga vergisi
  const damgaVergisi = toplamBrutMesaiUcreti * DAMGA_VERGISI_ORAN;

  // 8. Toplam kesinti
  const toplamKesinti =
    sgkIsciKesinti + issizlikIsciKesinti + gelirVergisi + damgaVergisi;

  // 9. Net mesai ücreti
  const netMesaiUcreti = Math.max(0, toplamBrutMesaiUcreti - toplamKesinti);

  // 10. İşveren ek maliyeti
  const sgkIsverenMaliyet = toplamBrutMesaiUcreti * SGK_ISVEREN_ORAN;
  const issizlikIsverenMaliyet = toplamBrutMesaiUcreti * ISSIZLIK_ISVEREN_ORAN;
  const toplamIsverenEkMaliyet = sgkIsverenMaliyet + issizlikIsverenMaliyet;
  const toplamIsverenMaliyet = toplamBrutMesaiUcreti + toplamIsverenEkMaliyet;

  // 11. Yıllık sınır kontrolü
  const yillikSinirUyarisi = guvenliSaat > YILLIK_FAZLA_MESAI_SINIRI;
  const kalanYillikKota = Math.max(0, YILLIK_FAZLA_MESAI_SINIRI - guvenliSaat);

  return {
    brutMaas: guvenliMaas,
    mesaiSaati: guvenliSaat,
    mesaiTipiLabel: getMesaiTipiLabel(mesaiTipi),
    katsayi,
    saatlikBrutUcret,
    mesaiSaatlikBrutUcret,
    toplamBrutMesaiUcreti,
    sgkIsciKesinti,
    issizlikIsciKesinti,
    damgaVergisi,
    gelirVergisiMatrahi,
    gelirVergisi,
    toplamKesinti,
    netMesaiUcreti,
    sgkIsverenMaliyet,
    issizlikIsverenMaliyet,
    toplamIsverenEkMaliyet,
    toplamIsverenMaliyet,
    yillikSinirUyarisi,
    kalanYillikKota,
  };
}

// ─── Mesai Tipi Seçenekleri (UI için) ─────────────────────────

export const MESAI_TIPI_SECENEKLERI: {
  value: MesaiTipi;
  label: string;
  aciklama: string;
  katsayiLabel: string;
}[] = [
  {
    value: "fazla_calisma",
    label: "Fazla Çalışma",
    aciklama: "Haftalık 45 saati aşan çalışma",
    katsayiLabel: "×1,5 (%50 zamlı)",
  },
  {
    value: "fazla_surelerle",
    label: "Fazla Sürelerle Çalışma",
    aciklama: "Sözleşme süresi ile 45 saat arası",
    katsayiLabel: "×1,25 (%25 zamlı)",
  },
  {
    value: "hafta_tatili",
    label: "Hafta Tatili Çalışması",
    aciklama: "Pazar veya hafta tatili günü çalışma",
    katsayiLabel: "×2 (2 kat)",
  },
  {
    value: "resmi_tatil",
    label: "Resmi Tatil / Bayram",
    aciklama: "Ulusal bayram ve genel tatil günü çalışma",
    katsayiLabel: "×2 (2 kat)",
  },
  {
    value: "gece_mesai",
    label: "Gece Mesaisi",
    aciklama: "20:00 – 06:00 arası çalışma",
    katsayiLabel: "×1,5 (%50 zamlı)",
  },
];
